import { useToast } from "@/hooks/use-toast";
import {
   addEdge,
   Background,
   Connection,
   Controls,
   MiniMap,
   OnSelectionChangeParams,
   ReactFlow,
   ReactFlowProvider,
   type Edge as RFEdge,
   type Node as RFNode,
   useEdgesState,
   useNodesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
   MouseEvent as ReactMouseEvent,
   useCallback,
   useEffect,
   useRef,
   useState,
   useMemo,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useAdminStore } from "../../../store/useAdminStore";
import { ImageInNode, Item, NodeType } from "../../../types/productTypes";
import fetchDb from "../../../utils/customRequestFunction";
import { ConnectBranchesForm } from "./ConnectBranches";
import ContextMenu from "./ContextMenu";
import CustomEdge from "./CustomEdge"; // Import the custom edge
import { ImagesNode } from "./ImageNode";

/**
 * Represents a node in the application's data (NOT the React Flow node).
 */
export interface AppNode {
   id: number;
   nodeLabel?: string;
   type?: NodeType;
   imagesInNode: ImageInNode[];
   incomingEdges: AppEdge[];
   outgoingEdges: AppEdge[];
   previousNode?: number;
   nextNode?: number;
}
/**
 * Represents an edge in the application's data (NOT the React Flow edge).
 */
export interface AppEdge {
   id: number;
   sourceNodeId: number;
   targetNodeId: number;
   conditionItems: Item[];
}
/**
 * The shape of adjacency graph data from server or your custom builder
 * (If you're using a separate adjacency-based response).
 */
export interface GraphResponse {
   graph: Record<
      number,
      {
         nodeId: number;
         nodeLabel: string;
         children: number[];
         // etc.
      }
   >;
   nodes: AppNode[];
}
/**
 * For multi-node selection in context menu,
 */
export interface SelectedAppNode {
   id: number;
   nodeLabel?: string;
   type?: string; // e.g., "CONNECTOR", "OPTION", etc.
}

export interface OptionPairGraphProps {
   optionPairId: number;
}
const nodeTypes = { imagesNode: ImagesNode };

const edgeTypes = { custom: CustomEdge };

export default function OptionPairGraph({
   optionPairId,
}: OptionPairGraphProps) {
   // React Flow state: typed as RFNode and RFEdge arrays
   const [rfNodes, setRfNodes, onNodesChange] = useNodesState<RFNode>([]);
   const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState<RFEdge>([]);
   console.log("optionPairIdFrom", optionPairId);
   // Track your "App" nodes that are selected in the UI
   const [selectedNodes, setSelectedNodes] = useState<RFNode[]>([]);

   // Handle context menu state
   const [contextMenu, setContextMenu] = useState<{
      top?: number;
      left?: number;
      right?: number;
      bottom?: number;
   } | null>(null);

   // Flag for showing connect branches form
   const [showConnectForm, setShowConnectForm] = useState(false);

   // Refs for positioning / instance
   const reactFlowWrapper = useRef<HTMLDivElement | null>(null);

   // Custom hooks
   const { toast } = useToast();
   const { refreshGraphData, fetchGraphData, clearGraphCache } =
      useAdminStore();

   // Additional state
   const [mainImage, setMainImage] = useState<any>(null);

   // Add loading state
   const [isLoading, setIsLoading] = useState(false);

   // Add progress state
   const [progress, setProgress] = useState({
      isVisible: false,
      message: "",
      percent: 0,
   });

   // ----------------------------------------------------------------
   // Fetch Graph Data
   // ----------------------------------------------------------------

   // Connect new edge
   const onConnectHandler = useCallback(
      async (connection: Connection) => {
         try {
            // Add edge to React Flow state
            setRfEdges((eds) => addEdge(connection, eds));

            // If we have source and target nodes, create the branch connection
            if (connection.source && connection.target) {
               await fetchDb.post("/api/option-pair/branch", {
                  sourceNodeId: connection.source,
                  targetNodeId: connection.target,
               });

               // Refresh graph data to get updated state
               await refreshGraphData();
            }
         } catch (error) {
            console.error("Error creating branch:", error);
            toast({
               title: "Error",
               description: "Failed to create branch connection",
               variant: "destructive",
            });

            // Revert the edge if the API call fails
            setRfEdges((eds) =>
               eds.filter(
                  (e) =>
                     e.source !== connection.source ||
                     e.target !== connection.target
               )
            );
         }
      },
      [setRfEdges, toast, refreshGraphData]
   );

   // Fetch main image for the option pair
   const fetchMainImage = useCallback(async () => {
      try {
         console.log(`Fetching main image for option pair ${optionPairId}`);
         const response = await fetchDb.get(
            `/api/option-pair/${optionPairId}/main-image`
         );
         console.log("Main image response:", response.data);
         if (response.data) {
            setMainImage(response.data);
         }
      } catch (error) {
         console.error("Error fetching main image:", error);
      }
   }, [optionPairId]);

   // Enhanced fetch graph data with loading state
   const fetchEnhancedGraphData = useCallback(async () => {
      try {
         setIsLoading(true);

         // First fetch the main image
         await fetchMainImage();

         // Then fetch the graph data
         await fetchGraphData(
            optionPairId,
            (nodes) => {
               // Find the root node
               const rootNode = nodes.find(
                  (node) => node.data.label === "root"
               );

               if (!rootNode) {
                  console.error("No root node found in graph data");
                  setRfNodes(nodes);
                  return;
               }

               console.log("Found root node:", rootNode.id);

               // Enhance the nodes with main image data
               const enhancedNodes = nodes.map((node) => {
                  if (node.data.label === "root") {
                     console.log(
                        "Enhancing root node with main image:",
                        mainImage
                     );
                     console.log(
                        "Adding optionPairId to root node:",
                        optionPairId
                     );

                     return {
                        ...node,
                        data: {
                           ...node.data,
                           mainImage: mainImage,
                           optionPairId: optionPairId,
                        },
                     };
                  }
                  return node;
               });

               setRfNodes(enhancedNodes);
            },
            setRfEdges
         );
      } catch (error) {
         console.error("Error in fetchEnhancedGraphData:", error);
         toast({
            title: "Error",
            description: "Failed to load graph data",
            variant: "destructive",
         });
      } finally {
         setIsLoading(false);
      }
   }, [
      optionPairId,
      fetchGraphData,
      fetchMainImage,
      mainImage,
      setRfNodes,
      setRfEdges,
   ]);

   useEffect(() => {
      fetchEnhancedGraphData();
   }, [optionPairId, fetchEnhancedGraphData]);

   useEffect(() => {
      console.log("rfEdges", rfEdges);
      console.log("rfNodes", rfNodes);
   }, [rfEdges, rfNodes]);

   // Right-click anywhere on the ReactFlow canvas
   const onContextMenuHandler = useCallback(
      (event: ReactMouseEvent) => {
         event.preventDefault();
         console.log("selectedNodes", selectedNodes);
         if (!reactFlowWrapper.current) return;

         const rect = reactFlowWrapper.current.getBoundingClientRect();

         const top = event.clientY - rect.top;
         const left = event.clientX - rect.left;

         setContextMenu({
            top: top < rect.height - 200 ? top : undefined,
            left: left < rect.width - 200 ? left : undefined,
            right: left >= rect.width - 200 ? rect.width - left : undefined,
            bottom: top >= rect.height - 200 ? rect.height - top : undefined,
         });
      },
      [selectedNodes]
   );

   // Close context menu on pane click
   const onPaneClickHandler = useCallback(() => {
      setContextMenu(null);
   }, []);

   // Track selected nodes
   const onSelectionChangeHandler = useCallback(
      (params: OnSelectionChangeParams) => {
         // params.nodes is an array of React Flow nodes that are selected
         const selectedRFNodes = params.nodes ?? [];
         setSelectedNodes([...selectedRFNodes]);
      },
      []
   );
   console.log("rfNodes", rfNodes);
   console.log("rfEdges", rfEdges);

   // Add this function to properly refresh the graph data including the main image
   const refreshFullGraphData = useCallback(async () => {
      try {
         console.log("Refreshing full graph data including main image");
         setIsLoading(true);

         // Clear the cache for this option pair to force a fresh fetch
         clearGraphCache(optionPairId);

         // Reset states to ensure clean refresh
         setRfNodes([]);
         setRfEdges([]);

         // Fetch everything fresh
         await fetchEnhancedGraphData();
      } catch (error) {
         console.error("Error refreshing full graph data:", error);
         toast({
            title: "Error",
            description: "Failed to refresh graph data",
            variant: "destructive",
         });
      } finally {
         setIsLoading(false);
      }
   }, [fetchEnhancedGraphData, optionPairId, clearGraphCache]);

   // Pass this function to the context menu
   const contextMenuWithRefresh = useMemo(() => {
      return contextMenu ? (
         <ContextMenu
            top={contextMenu.top}
            left={contextMenu.left}
            right={contextMenu.right}
            bottom={contextMenu.bottom}
            selectedNodes={selectedNodes}
            onClose={onPaneClickHandler}
            optionPairId={optionPairId}
            onRefresh={refreshFullGraphData}
         />
      ) : null;
   }, [
      contextMenu,
      selectedNodes,
      onPaneClickHandler,
      optionPairId,
      refreshFullGraphData,
   ]);

   // ----------------------------------------------------------------
   // Rendering
   // ----------------------------------------------------------------
   return (
      <DndProvider backend={HTML5Backend}>
         <div
            className="w-full h-full relative"
            style={{ display: "flex", height: "100vh" }}
            ref={reactFlowWrapper}
            onContextMenu={onContextMenuHandler}
         >
            <div style={{ flexGrow: 1, position: "relative" }}>
               <ReactFlowProvider>
                  {isLoading && (
                     <div className="absolute inset-0 bg-white/70 z-50 flex items-center justify-center">
                        <div className="flex flex-col items-center">
                           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-2"></div>
                           <p className="text-blue-500 font-medium">
                              Loading graph...
                           </p>
                        </div>
                     </div>
                  )}
                  <ReactFlow
                     nodeTypes={nodeTypes}
                     edgeTypes={edgeTypes} // Register custom edge types
                     nodes={rfNodes}
                     edges={rfEdges}
                     onNodesChange={onNodesChange}
                     onEdgesChange={onEdgesChange}
                     onConnect={onConnectHandler}
                     onSelectionChange={onSelectionChangeHandler}
                     onPaneClick={onPaneClickHandler}
                     fitView
                     style={{ backgroundColor: "#F7F9FB" }}
                  >
                     <Controls />
                     <MiniMap />
                     <Background variant="dots" gap={12} size={1} />
                  </ReactFlow>

                  {/* Right-click context menu */}
                  {contextMenuWithRefresh}

                  {/* Optional: Connect Branches form */}
                  {showConnectForm && (
                     <ConnectBranchesForm
                        optionPairId={optionPairId}
                        onClose={() => setShowConnectForm(false)}
                        onConnected={() =>
                           fetchGraphData(optionPairId, setRfNodes, setRfEdges)
                        }
                     />
                  )}

                  {/* Progress indicator component */}
                  {progress.isVisible && (
                     <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-3 z-50">
                        <div className="flex justify-between mb-1">
                           <span className="text-sm font-medium">
                              {progress.message}
                           </span>
                           <span className="text-sm font-medium">
                              {progress.percent}%
                           </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                           <div
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                              style={{ width: `${progress.percent}%` }}
                           ></div>
                        </div>
                     </div>
                  )}
               </ReactFlowProvider>
            </div>
         </div>
      </DndProvider>
   );
}
